import { defineComponent } from "vue";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";

import config from "../../../common/config";
import { IEmployeeCalendarRequestParams, IEmployeeCalendarResponse } from "../../../services/user-service";
import {
    ITaskByUserRequest,
    ITaskByUserResponse,
    IUserScheduleSummary,
    TaskService
} from "../../../services/task.service";
import { ToastrService } from "../../../services/toastr.service";
import { IEmployeeCalendarFilterOptions } from "../../../resources-vue/vue-interfaces/i-calendar";
import { EmployeeCalendarModel } from "./EmployeeCalendar.model";
import { PAGE_FAIL_RESPONSE } from "../../../resources/elements/pagination";
import { CalendarViewTypeEnum } from "../../../enums/CalendarViewTypeEnum";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import { useApp } from "../../App/useApp";
import eventBus from "../../../utilities/eventBus";
import { ApplicationEnum } from "../../../enums/ApplicationEnum";
import { AxxessIntelligenceService } from "../../../services/axxess-intelligence.service";
import { IGetAgency } from "../../../services/agency.service";

import DailyCalendarContent from "./components/DailyCalendarContent/DailyCalendarContent.vue";
import EmployeeCalendarContent from "./components/EmployeeCalendarContent/EmployeeCalendarContent.vue";
import Pagination from "../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import CalendarFilters from "../components/CalendarFilters/CalendarFilters.vue";
import EmployeeMap from "./components/EmployeeMap/EmployeeMap.vue";
import SummaryView from "./components/SummaryView/SummaryView.vue";
import FindNearbyClients from "./components/FindNearbyClients/FindNearbyClients.vue";

export default defineComponent({
    components: {
        CalendarFilters,
        DailyCalendarContent,
        EmployeeCalendarContent,
        Pagination,
        EmployeeMap,
        SummaryView,
        FindNearbyClients
    },

    data(vm) {
        return {
            _taskService: null as TaskService,
            _toastrService: null as ToastrService,
            _axxessIntelligenceService: null as AxxessIntelligenceService,
            startDate: null as Date,
            endDate: null as Date,
            dailyDate: new Date(Date.now()) as Date,
            listView: CalendarViewTypeEnum.Weekly as string,
            isEmployeeLoading: true as boolean,
            pageSize: 15 as number,
            isError: false as boolean,
            employeeCalendarPagination: null as any,
            employeeFilterOptions: {
                pageLength: 15,
                page: 1,
                term: "",
                agencies: [],
                locationIds: [],
                scheduleFilter: 0,
                startDate: "",
                endDate: "",
                userIds: []
            } as IEmployeeCalendarRequestParams,
            taskFilterOptions: {
                start: null,
                end: null,
                userIds: [],
                agencyIds: []
                // weekdayRange: 1
            } as ITaskByUserRequest,
            employees: [] as EmployeeCalendarModel[],
            tasks: [] as ITaskByUserResponse[],
            tasksMap: null as any,
            Schedules: [] as ITaskByUserResponse[],
            allUsers: [] as string[],
            tooltip: `<div class='evv-statuses'>
           <p> <i class='evv-adjusted signed'></i> Adjusted Completed EVV</p>
           <p><i class='evv-adjusted in-progress'></i> Adjusted InProgress EVV</p>
           <p><a class='evv'><i class='fa fa-home fa-md'></i></a> In Progress EVV</p>
           <p><a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a> Not Started EVV</p>
           <p><a class='evv'><i class='fa fa-home fa-md signed'></i></a> Completed EVV</p>
            </div>`,
            CalendarViewTypeEnum,
            showFindNearByClientsView: false as boolean,
            isPremiumByAgency: null as Record<string, boolean> | null,
            agencyList: [] as IGetAgency[],
            organizationId: "",
            selectedEmployeeForFindNearbyClient: null as EmployeeCalendarModel
        };
    },
    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._axxessIntelligenceService = Container.instance.get(AxxessIntelligenceService);
        this.employeeCalendarPagination = this.$refs?.employeeCalendarPagination as typeof Pagination;
    },
    async mounted() {
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.employeeCalendarPagination = this.$refs?.employeeCalendarPagination as typeof Pagination;
        this.agencyList = this.organizations?.find(
            (organization: { id: string }) => organization.id === this.organizationId
        ).agencies;
        const agenciesList = [...new Set(this.agencyList.map((agency) => agency.id.split("_")[0]))];
        const resps = await this._axxessIntelligenceService.getAiPermissionOfAgencies({
            AgencyIds: agenciesList
        });
        this.isPremiumByAgency = resps?.PremiumStatuses;
    },

    computed: {
        ...mapState(useApp, ["showMainHeader", "organizations"]),
        hasData() {
            return this.employees?.length > 0;
        },
        showCalendar() {
            return this.listView !== CalendarViewTypeEnum.Map && !this.showFindNearByClientsView;
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        handleViewChange(type: string) {
            this.listView = type;
            if (type === CalendarViewTypeEnum.Daily) {
                this.dailyDateUpdate({ startDate: this.dailyDate, endDate: this.dailyDate });
            } else {
                this.updateDateRange({ startDate: this.startDate, endDate: this.endDate });
            }
        },
        async updateDateRange(options: any, isWeekRangeChanged: boolean = true) {
            if (isWeekRangeChanged) {
                this.startDate = options.startDate;
                this.endDate = options.endDate;
            }
            this.employeeFilterOptions.startDate = this.taskFilterOptions.start = this.formatDate(this.startDate);
            this.employeeFilterOptions.endDate = this.taskFilterOptions.end = this.formatDate(this.endDate);
            if (this.listView !== CalendarViewTypeEnum.SummaryView) {
                await this.pageReset();
            } else {
                eventBus.emit("callSummaryReset", this.employeeFilterOptions);
            }
        },

        async dailyDateUpdate(options: any, isWeekRangeChanged: boolean = true) {
            if (isWeekRangeChanged) {
                this.dailyDate = options.startDate;
            }
            this.employeeFilterOptions.startDate = this.taskFilterOptions.start = this.formatDate(options.startDate);
            this.employeeFilterOptions.endDate = this.taskFilterOptions.end = this.formatDate(options.endDate);
            await this.pageReset();
        },

        formatDate(input: Date) {
            return `${input.getMonth() + 1}/${input.getDate()}/${input.getFullYear()}`;
        },

        async employeeRefresh(options: IEmployeeCalendarFilterOptions, isWeekRangeChanged: boolean) {
            this.taskFilterOptions.agencyIds = [...options.agencies];
            this.taskFilterOptions.locationId = [...options.locationIds];
            this.employeeFilterOptions = {
                ...this.employeeFilterOptions,
                ...options
            };
            if (this.listView === CalendarViewTypeEnum.Weekly || this.listView === CalendarViewTypeEnum.SummaryView) {
                this.updateDateRange(options, isWeekRangeChanged);
            } else {
                this.dailyDateUpdate(options);
            }
        },

        async pageReset() {
            this.isError = false;
            await this.employeeCalendarPagination?.reset();
        },
        async pageChanged(pageNumber: number, pageSize: number = 15) {
            try {
                if (
                    this.employeeFilterOptions?.agencies?.length > 0 &&
                    this.employeeFilterOptions.locationIds.length > 0
                ) {
                    this.isError = false;
                    this.employees = [];
                    this.employeeFilterOptions.page = pageNumber;
                    this.isEmployeeLoading = true;
                    let data = await this._taskService.getUsersAndTaskForCalendar({
                        ...this.employeeFilterOptions,
                        AgencyIds: this.employeeFilterOptions.agencies
                    });
                    if (data) {
                        this.Schedules = data.schedule;
                        this.taskFilterOptions.userIds = [];
                        let dataItems = data.User.Items ?? [];
                        this.employees = dataItems.map((item: any) => new EmployeeCalendarModel(item));
                        this.employees?.forEach((user) => {
                            user.Agencies.forEach((op) => {
                                this.taskFilterOptions.userIds.push(op.UserId);
                            });
                        });
                        await this.updateTaskRefresh();
                        this.allUsers = this.employees.map((emp: IEmployeeCalendarResponse) => emp.Id);
                        this.isEmployeeLoading = false;
                        return {
                            totalPageCount: data.User.PageCount,
                            success: data.User.ItemCount > 0,
                            totalCount: data.User.ItemCount
                        };
                    } else {
                        return PAGE_FAIL_RESPONSE;
                    }
                } else {
                    this.employees = [];
                    this.taskFilterOptions.userIds = [];
                    return PAGE_FAIL_RESPONSE;
                }
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            }
        },

        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },

        async taskRefresh() {
            try {
                if (this.taskFilterOptions?.userIds?.length > 0) {
                    const tasks = await this._taskService.getTasksByUsers(this.taskFilterOptions);
                    this.taskSort(tasks);
                }
            } catch (e) {
                console.error(e);
            }
        },

        updateTaskRefresh() {
            const tasks = this.Schedules;
            this.taskSort(tasks);
        },

        taskSort(tasks: ITaskByUserResponse[]) {
            try {
                if (this.taskFilterOptions?.userIds?.length > 0) {
                    tasks.sort((a: any, b: any) => {
                        let aCriteria: "TimeIn" | "StartTime";
                        let bCriteria: "TimeIn" | "StartTime";

                        if (a.State === 4) {
                            aCriteria = "TimeIn";
                        } else {
                            aCriteria = "StartTime";
                        }

                        if (b.State === 4) {
                            bCriteria = "TimeIn";
                        } else {
                            bCriteria = "StartTime";
                        }

                        return moment(a[aCriteria]).isAfter(moment(b[bCriteria]))
                            ? 1
                            : moment(a[aCriteria]).isSame(moment(b[bCriteria]))
                              ? 0
                              : -1;
                    });
                    this.tasks = tasks;
                }
            } catch (e) {
                console.error(e);
            }
        },
        toggleFindNearByClientsView(details?: any) {
            if (this.showFindNearByClientsView && !details) {
                this.selectedEmployeeForFindNearbyClient = null;
                this.showFindNearByClientsView = false;
                return;
            }

            if (!details.Latitude || !details.Longitude) {
                return this._toastrService.error({
                    title: "Error",
                    message: "The selected employee's location details are unavailable."
                });
            }

            this.selectedEmployeeForFindNearbyClient = {
                ...details,
                Application:
                    details?.Application == ApplicationEnum.AxxessHospice
                        ? ApplicationEnum.AxxessHospiceFE
                        : details?.Application,
                Credentials: details?.Agencies?.find((agency) => agency.AgencyId === details?.AgencyId)?.Credentials
            };
            this.showFindNearByClientsView = true;
        }
    }
});
